import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

const LOGO_URL =
    "https://customer-assets.emergentagent.com/job_55a9f4cb-4c33-47bd-86a7-80fe678bbbdb/artifacts/wavtipa0_PROKSY%20RESEARCH%20%282%29.png";

const links = [
    { to: "/", label: "Home" },
    { to: "/services", label: "Services" },
    { to: "/industries", label: "Industries" },
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 14);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
        window.scrollTo({ top: 0, behavior: "instant" });
    }, [location.pathname]);

    return (
        <motion.header
            initial={{ y: -40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }}
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "py-3 bg-[#050B1A]/85 backdrop-blur-xl border-b border-white/5"
                    : "py-5 bg-transparent"
            }`}
            data-testid="main-navbar"
        >
            <div className="pr-container flex items-center justify-between">
                <Link
                    to="/"
                    className="flex items-center gap-3 group"
                    data-testid="navbar-logo-link"
                >
                    <div className="relative w-10 h-10 rounded-xl overflow-hidden border border-white/10 bg-black/30">
                        <img
                            src={LOGO_URL}
                            alt="Proksy Research"
                            className="w-full h-full object-cover"
                        />
                    </div>
                    <div className="leading-tight">
                        <p className="font-outfit font-bold text-base tracking-wide text-white">
                            PROKSY
                        </p>
                        <p className="font-outfit text-[10px] tracking-[0.32em] text-blue-400">
                            RESEARCH
                        </p>
                    </div>
                </Link>

                <nav className="hidden lg:flex items-center gap-1 pr-glass rounded-full px-2 py-1.5">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/"}
                            className={({ isActive }) =>
                                `relative px-4 py-2 rounded-full text-sm font-outfit font-medium transition-colors ${
                                    isActive
                                        ? "text-white"
                                        : "text-slate-400 hover:text-white"
                                }`
                            }
                            data-testid={`nav-link-${l.label.toLowerCase()}`}
                        >
                            {({ isActive }) => (
                                <>
                                    {isActive && (
                                        <motion.span
                                            layoutId="nav-pill"
                                            className="absolute inset-0 rounded-full bg-blue-500/15 border border-blue-400/30"
                                            transition={{
                                                type: "spring",
                                                bounce: 0.2,
                                                duration: 0.55,
                                            }}
                                        />
                                    )}
                                    <span className="relative">{l.label}</span>
                                </>
                            )}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-3">
                    <Link
                        to="/portal/login"
                        className="text-sm font-outfit font-medium text-slate-300 hover:text-white px-4 py-2 rounded-full hover:bg-white/5 transition-colors"
                        data-testid="navbar-portal-login"
                    >
                        Sign in
                    </Link>
                    <Link
                        to="/contact"
                        className="btn-primary text-sm py-2.5 px-5"
                        data-testid="navbar-cta-quote"
                    >
                        Get a Quote
                        <ArrowUpRight className="w-4 h-4" />
                    </Link>
                </div>

                <button
                    onClick={() => setOpen((v) => !v)}
                    className="lg:hidden w-10 h-10 rounded-xl pr-glass flex items-center justify-center text-white"
                    aria-label="Menu"
                    data-testid="mobile-menu-toggle"
                >
                    {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="lg:hidden overflow-hidden"
                        data-testid="mobile-menu"
                    >
                        <div className="pr-container py-5 flex flex-col gap-2">
                            {links.map((l) => (
                                <NavLink
                                    key={l.to}
                                    to={l.to}
                                    end={l.to === "/"}
                                    className={({ isActive }) =>
                                        `px-4 py-3 rounded-xl font-outfit font-medium ${
                                            isActive
                                                ? "bg-blue-500/15 border border-blue-400/30 text-white"
                                                : "text-slate-300 border border-white/5"
                                        }`
                                    }
                                    data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                                >
                                    {l.label}
                                </NavLink>
                            ))}
                            <Link
                                to="/portal/login"
                                className="px-4 py-3 rounded-xl font-outfit font-medium text-slate-300 border border-white/5"
                                data-testid="mobile-portal-login"
                            >
                                Sign in
                            </Link>
                            <Link
                                to="/contact"
                                className="btn-primary justify-center mt-2"
                                data-testid="mobile-cta-quote"
                            >
                                Get a Quote
                                <ArrowUpRight className="w-4 h-4" />
                            </Link>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.header>
    );
}
